import * as Types from 'echarts/types/dist/echarts';

import { getThemeColor } from '~/design-tokens/themes/theme-utils';
import { ThemeName } from '~/design-tokens/themes/themes';

import { LEGEND_FONT_SIZE } from '../apache-config';

const UNIFORM_SYMBOL_SIZE = 8;
const MINIMUM_SYMBOL_SIZE = 4;
const AXIS_BUBBLE_MULTIPLIER = 4;
const SYMBOL_SIZE_BUFFER = 10;

export type DotChartsNames =
  | 'apache-axis-bubble'
  | 'apache-bubble'
  | 'apache-bubble-time';

interface BasicData {
  name: string;
  data: (number | null)[][];
}

interface SeriesWithTime {
  year: string;
  series: BasicData[];
}

export type BasicSeries = BasicData[];
export type BubbleTimeSeries = SeriesWithTime[];

export type DotChartSeries = BasicSeries | BubbleTimeSeries;

const SINGLE_LINE_HEIGHT = 20;
const LINE_TOP_PADDING = 5;

const isBasicSeries = (series: DotChartSeries): series is BasicSeries => {
  return series.length > 0 && !('year' in series[0]);
};

const isBubbleTimeSeries = (
  series: DotChartSeries
): series is BubbleTimeSeries => {
  return series.length > 0 && 'year' in series[0];
};

export const tsTypeMap: Record<DotChartsNames, string> = {
  'apache-bubble': `
    interface DotChartProps {
      chartType: 'apache-bubble';
      series: {
        name: string;
        data: (number | null)[][];
      }[];
      axisLabels?: string[];
   }`,
  'apache-bubble-time': `
    interface BasicData {
      name: string
      data: (number | null)[][]
    }

    interface SeriesWithTime {
      year: string
      series: BasicData[]
    }

    type BubbleTimeSeries = SeriesWithTime[]

    interface DotChartProps {
      chartType: 'apache-bubble-time';
      series: BubbleTimeSeries;
      axisLabels?: string[];
    }
  `,
  'apache-axis-bubble': `
    interface DotChartProps {
      chartType: 'apache-axis-bubble';
      series: {
        year: string;
        data: (number | null)[][];
      }[];
      axisLabels?: string[];
   }`,
};

export const jsonSchemaTypeMap: Record<DotChartsNames, {}> = {
  'apache-bubble': {
    type: 'object',
    required: ['chartType', 'series'],
    properties: {
      chartType: {
        type: 'string',
      },
      series: {
        type: 'array',
        items: {
          type: 'object',
          required: [],
          properties: {
            name: {
              type: 'string',
            },
            data: {
              type: 'array',
              items: {
                type: 'array',
                items: {
                  type: ['number', 'null'],
                },
              },
            },
          },
        },
      },
      axisLabels: {
        type: 'array',
        items: {
          type: 'string',
        },
      },
    },
  },
  'apache-bubble-time': {
    type: 'object',
    required: ['chartType', 'series'],
    properties: {
      chartType: {
        type: 'string',
      },
      series: {
        type: 'array',
        items: {
          type: 'object',
          required: ['year', 'series'],
          properties: {
            year: {
              type: 'string',
            },
            series: {
              type: 'array',
              items: {
                type: 'object',
                required: ['name', 'data'],
                properties: {
                  name: {
                    type: 'string',
                  },
                  data: {
                    type: 'array',
                    items: {
                      type: 'array',
                      items: {
                        type: ['number', 'null'],
                      },
                    },
                  },
                },
              },
            },
          },
        },
      },
      axisLabels: {
        type: 'array',
        items: {
          type: 'string',
        },
      },
    },
  },
  'apache-axis-bubble': {
    type: 'object',
    required: ['chartType', 'series'],
    properties: {
      chartType: {
        type: 'string',
      },
      series: {
        type: 'array',
        items: {
          type: 'object',
          required: ['name', 'data'],
          properties: {
            name: {
              type: 'string',
            },
            data: {
              type: 'array',
              items: {
                type: 'array',
                items: {
                  type: ['number', 'null'],
                },
              },
            },
          },
        },
      },
      axisLabels: {
        type: 'array',
        items: {
          type: 'string',
        },
      },
    },
  },
};

interface LogScaleTransformArgs {
  value: number;
  inputMin: number;
  inputMax: number;
  outputMin: number;
  outputMax: number;
}

export const logScaleTransform = ({
  value,
  inputMin,
  inputMax,
  outputMin,
  outputMax,
}: LogScaleTransformArgs) => {
  const maxValue = Math.max(inputMin, Math.min(value, inputMax));
  const safeMin = inputMin <= 0 ? 1 : inputMin;
  const safeMax = inputMax <= 0 ? 1 : inputMax;

  if (safeMin === safeMax) {
    return outputMin;
  }

  const logMin = Math.log(safeMin);
  const logMax = Math.log(safeMax);
  const logValue = Math.log(maxValue);
  const scale = (logValue - logMin) / (logMax - logMin);

  return outputMin + scale * (outputMax - outputMin);
};

interface CheckIsBubbleSizeSameArgs {
  chartType: DotChartsNames;
  series: DotChartSeries;
}

export const checkIsBubbleSizeSame = ({
  chartType,
  series,
}: CheckIsBubbleSizeSameArgs) => {
  switch (chartType) {
    case 'apache-bubble-time':
      if (isBubbleTimeSeries(series)) {
        const dataValuesTime = series
          .flatMap(item => item.series)
          .flatMap(seriesItem =>
            seriesItem.data.map(dataItem => dataItem[dataItem.length - 1])
          )
          .filter(value => value);
        const isAllValuesSame = dataValuesTime.every(
          i => i === dataValuesTime[0]
        );

        return !!dataValuesTime.length && isAllValuesSame;
      }

      return false;

    case 'apache-axis-bubble':
      if (isBasicSeries(series)) {
        const dataValuesAxis = series
          .flatMap(series => series.data)
          .map(dataItem => dataItem[dataItem.length - 1])
          .filter(value => value);
        const isAllValuesSame = dataValuesAxis.every(
          i => i === dataValuesAxis[0]
        );

        return !!dataValuesAxis.length && isAllValuesSame;
      }

      return false;

    case 'apache-bubble':
      if (isBasicSeries(series)) {
        const dataValues = series
          .flatMap(item => item.data)
          .map(dataItem => dataItem[dataItem.length - 1])
          .filter(value => value);
        const isAllValuesSame = dataValues.every(i => i === dataValues[0]);

        return !!dataValues.length && isAllValuesSame;
      }

      return false;

    default:
      return false;
  }
};

export const formatDotSeries = ({
  series,
  options,
  chartWidth,
  isSizeIdentical,
}: {
  options: { chartType: DotChartsNames; isEffectScatter?: boolean };
  series: DotChartSeries;
  chartWidth: number;
  isSizeIdentical: boolean;
}): Types.SeriesOption[] => {
  switch (options.chartType) {
    case 'apache-axis-bubble':
      if (isBasicSeries(series)) {
        return series.map((item, index) => ({
          singleAxisIndex: index,
          coordinateSystem: 'singleAxis',
          type: 'scatter',
          symbolSize: (dataItem: number[]) =>
            isSizeIdentical
              ? UNIFORM_SYMBOL_SIZE
              : dataItem[1] * AXIS_BUBBLE_MULTIPLIER,
          data: item.data,
        }));
      }

      return [];

    case 'apache-bubble':
      if (isBasicSeries(series)) {
        const dataValues = series.map(i => i.data.map(i => i[2] ?? 0)).flat();
        const hasZValue = series
          .flatMap(i => i.data)
          .every(i => {
            return i.length === 3;
          });

        const maxValue = Math.max(...dataValues);
        const minValue = Math.min(...dataValues);
        return series.map(i => {
          return {
            ...i,
            type: 'scatter',
            symbolSize: (data: number[]) => {
              if (!hasZValue || maxValue === 0 || isSizeIdentical) {
                return UNIFORM_SYMBOL_SIZE;
              }

              const maxSize =
                (chartWidth * data[2]) / maxValue / SYMBOL_SIZE_BUFFER;

              if (maxValue < maxSize) {
                return Math.max(data[2], MINIMUM_SYMBOL_SIZE);
              }

              return logScaleTransform({
                value: data[2],
                inputMin: minValue,
                inputMax: maxValue,
                outputMin: MINIMUM_SYMBOL_SIZE,
                outputMax: Math.max(maxSize, MINIMUM_SYMBOL_SIZE),
              });
            },
            itemStyle: {
              opacity: 1,
              shadowBlur: 0,
            },
            emphasis: {
              focus: 'series',
              label: {
                formatter: '',
              },
            },
          };
        });
      }

      return [];

    case 'apache-bubble-time':
      if (isBubbleTimeSeries(series)) {
        return series.map(i => ({
          series: {
            data: i.series.map(i => i.data).flat(),
            symbolSize: UNIFORM_SYMBOL_SIZE,
            type: 'scatter',
            itemStyle: {
              opacity: 0.8,
            },
          },
        })) as Types.SeriesOption[];
      }

      return [];

    default: {
      return [];
    }
  }
};

export const chartSeries: Record<DotChartsNames, DotChartSeries> = {
  'apache-bubble': [
    {
      name: 'China',
      data: [
        [null, null, null],
        [8, 6, 59],
        [10, 1, 64],
        [5, 4, 35],
        [5, 7, 7],
      ],
    },
    {
      name: 'Japan',
      data: [
        [null, null, null],
        [10, 10, 61],
        [1, 1, 34],
        [6, 2, 40],
        [0, 1, 71],
      ],
    },
    {
      name: 'USA',
      data: [
        [null, null, null],
        [3, 1, 86],
        [3, 9, 18],
        [1, 6, 45],
        [4, 5, 17],
      ],
    },
  ],
  'apache-bubble-time': [
    {
      year: '1960',
      series: [
        {
          name: 'China',
          data: [
            [null, null, null],
            [6, 3, 22],
            [4, 1, 58],
            [9, 5, 92],
            [8, 9, 72],
          ],
        },
        {
          name: 'Japan',
          data: [
            [null, null, null],
            [0, 6, 60],
            [10, 0, 86],
            [0, 3, 85],
            [7, 10, 63],
          ],
        },
        {
          name: 'USA',
          data: [
            [null, null, null],
            [0, 1, 5],
            [5, 7, 28],
            [2, 8, 62],
            [8, 5, 59],
          ],
        },
      ],
    },
    {
      year: '1980',
      series: [
        {
          name: 'China',
          data: [
            [null, null, null],
            [6, 6, 93],
            [8, 8, 52],
            [7, 5, 57],
            [3, 8, 18],
          ],
        },
        {
          name: 'Japan',
          data: [
            [null, null, null],
            [7, 3, 28],
            [3, 2, 83],
            [2, 4, 62],
            [10, 0, 58],
          ],
        },
        {
          name: 'USA',
          data: [
            [null, null, null],
            [2, 2, 59],
            [2, 0, 32],
            [5, 10, 19],
            [2, 3, 36],
          ],
        },
      ],
    },
    {
      year: '2000',
      series: [
        {
          name: 'China',
          data: [
            [null, null, null],
            [2, 8, 61],
            [3, 2, 14],
            [9, 4, 26],
            [4, 6, 9],
          ],
        },
        {
          name: 'Japan',
          data: [
            [null, null, null],
            [4, 0, 3],
            [7, 5, 19],
            [2, 8, 46],
            [8, 3, 45],
          ],
        },
        {
          name: 'USA',
          data: [
            [null, null, null],
            [1, 9, 59],
            [4, 9, 55],
            [10, 8, 35],
            [8, 3, 58],
          ],
        },
      ],
    },
  ],
  'apache-axis-bubble': [
    {
      name: 'Monday',
      data: [
        [0, 5],
        [null, null],
        [2, 0],
        [3, 0],
        [4, 0],
        [5, 0],
        [6, 0],
        [7, 0],
        [8, 0],
        [9, 0],
        [10, 0],
        [11, 2],
        [12, 4],
        [13, 1],
        [14, 1],
        [15, 3],
        [16, 4],
        [17, 6],
        [18, 4],
        [19, 4],
        [20, 3],
        [21, 3],
        [22, 2],
        [23, 5],
      ],
    },
    {
      name: 'Tuesday',
      data: [
        [0, 7],
        [1, 0],
        [2, 0],
        [3, 0],
        [4, 0],
        [5, 0],
        [6, 0],
        [7, 0],
        [8, 0],
        [9, 0],
        [10, 5],
        [11, 2],
        [12, 2],
        [13, 6],
        [14, 9],
        [15, 11],
        [16, 6],
        [17, 7],
        [18, 8],
        [19, 12],
        [20, 5],
        [21, 5],
        [22, 7],
        [23, 2],
      ],
    },
  ],
};

export const chartAxisLabels: Record<DotChartsNames, string[]> = {
  'apache-bubble': ['Stock Prices', 'Net Profit', 'Market Cap'],
  'apache-bubble-time': ['Stock Prices', 'Net Profit'],
  'apache-axis-bubble': ['Stock Prices', '', 'Market Cap'],
};

export const getAPIPropsDescription = (chartType: DotChartsNames) => {
  switch (chartType) {
    case 'apache-bubble':
      return [
        'Each series is grouped by country and represented by a three-dimension array, where the first value is the x-coordinate, the second value is the y-coordinate, and the third value is the size of the bubble.',
      ];

    case 'apache-bubble-time':
      return [
        'Each series is first grouped by year then by country, and represented as a three-dimensional array, where the first value is the x-coordinate, the second is the y-coordinate, and the third is the size of the bubble.',
      ];

    case 'apache-axis-bubble':
      return [
        'Each series is grouped by day of the week, and represented as a three-dimensional array, where the first value is the x-coordinate, the second is the y-coordinate, and the third is the size of the bubble.',
      ];

    default:
      return [];
  }
};

export const getFEDotChartProps = ({
  chartType,
  series,
  axisLabels,
  theme,
  isSizeIdentical,
}: {
  chartType: DotChartsNames;
  series: DotChartSeries;
  axisLabels?: string[];
  theme: ThemeName;
  isSizeIdentical: boolean;
}): Types.EChartsOption => {
  const zAxisInfo =
    !isSizeIdentical && axisLabels?.[2]
      ? {
          text: `Size: ${axisLabels?.[2]}`,
          bottom: 0,
          right: 7,
          textStyle: {
            color: getThemeColor('--text-default', theme),
            fontSize: LEGEND_FONT_SIZE,
            fontWeight: 400,
          },
        }
      : {};

  switch (chartType) {
    case 'apache-axis-bubble': {
      if (isBasicSeries(series)) {
        const days = series.map(i => i.name);
        const titles = days.map((day, idx) => ({
          top: `${idx * SINGLE_LINE_HEIGHT + LINE_TOP_PADDING}% `,
          text: day,
        }));
        return {
          animation: false,
          singleAxis: days.map((_, idx) => ({
            name: axisLabels?.[0],
            left: 100,
            right: 110,
            type: 'category',
            boundaryGap: false,
            top: `${idx * SINGLE_LINE_HEIGHT + LINE_TOP_PADDING}% `,
            height: '4%',
            nameTruncate: {
              maxWidth: 100,
            },
            axisLabel: {
              interval: 2,
            },
          })),
          title: [...titles, zAxisInfo],
        };
      }

      return {};
    }

    case 'apache-bubble-time': {
      if (isBubbleTimeSeries(series)) {
        return {
          animation: true,
          timeline: {
            axisType: 'category',
            orient: 'vertical',
            autoPlay: true,
            inverse: true,
            playInterval: 2000,
            right: 0,
            top: 20,
            bottom: 20,
            width: 55,
            symbol: 'none',
            checkpointStyle: {
              symbolSize: UNIFORM_SYMBOL_SIZE,
            },
            controlStyle: {
              showNextBtn: false,
              showPrevBtn: false,
              itemSize: 20,
            },
            data: series.map(i => i.year),
          },
          animationDurationUpdate: 1000,
          animationEasingUpdate: 'quinticInOut',
          title: zAxisInfo,
        };
      }

      return {};
    }

    default:
      return {
        animation: false,
        title: zAxisInfo,
      };
  }
};
